import React, { type ReactNode } from "react";
import {
  CALM_GUIDANCE,
  FAQ,
  FINAL_CTA,
  HERO,
  LIFE_AREAS,
  LIFE_GPS,
  STEPS_TO_CLARITY,
} from "./landingContent";
import { parseEmphasis, useLandingCopy, type HeadlineKey } from "./landingCopy";

const PLANET_GLYPHS = ["☉", "☽", "☿", "♀", "♂", "♃", "♄", "♅", "♆", "✦"];
const pad2 = (n: number): string => String(n).padStart(2, "0");

/* ---------- shared pieces ---------- */

/** Renders a title where `<em>…</em>` marks the italic accent phrase. */
export const Emphasis: React.FC<{ markup: string }> = ({ markup }) => (
  <>
    {parseEmphasis(markup).map((part, index) =>
      part.emphasis ? (
        <em key={index}>{part.text}</em>
      ) : (
        <React.Fragment key={index}>{part.text}</React.Fragment>
      ),
    )}
  </>
);

export const SectionShell: React.FC<{ id: string; children: ReactNode }> = ({
  id,
  children,
}) => (
  <section className="al-section" id={id}>
    <div className="al-wrap">{children}</div>
  </section>
);

interface SectionHeadProps {
  eyebrow?: string;
  /** Translated title with an italic accent. */
  titleKey: HeadlineKey;
  body?: string;
}

const SectionHead: React.FC<SectionHeadProps> = ({ eyebrow, titleKey, body }) => {
  const { tr, headline } = useLandingCopy();
  return (
    <div className="al-head" data-reveal>
      {eyebrow && <div className="al-eyebrow al-eyebrow--center">{tr(eyebrow)}</div>}
      <h2 className="al-h2">
        <Emphasis markup={headline(titleKey)} />
      </h2>
      {body && <p className="al-lead">{tr(body)}</p>}
    </div>
  );
};

/* ---------- hero ---------- */

/** "Astria — Your Personal Intelligence Companion": the name sits on its own line. */
const HERO_NAME = `${HERO.headline.split(" — ")[0]} —`;

export const HeroSection: React.FC<{ onAction: (action: string) => void }> = ({
  onAction,
}) => {
  const { lang, tr, headline } = useLandingCopy();
  const isEnglish = lang === "en";

  return (
    <div className="al-wrap al-hero" id="hero">
      <div className="al-hero__copy" data-reveal>
        <h1 className="al-hero__title">
          <span className="al-hero__line">{tr(HERO_NAME)}</span>
          <span className="al-hero__line">{headline("title")}</span>
        </h1>
        <p className="al-hero__sub">{isEnglish ? HERO.subheadline : headline("sub")}</p>
        <p className="al-lead al-hero__lead">
          {isEnglish ? HERO.body : headline("lead")}
        </p>
        <div className="al-hero__ctas">
          <button
            type="button"
            className="al-btn al-btn--primary"
            onClick={() => onAction(HERO.ctaPrimary.action)}
          >
            {headline("cta")} ✦
          </button>
          <button
            type="button"
            className="al-btn al-btn--ghost"
            onClick={() => onAction(HERO.ctaSecondary.action)}
          >
            {tr(HERO.ctaSecondary.label)}
          </button>
        </div>
      </div>

      <div className="al-hero__orbit" data-reveal>
        <div className="al-hero__glow" aria-hidden="true" />
        <img
          className="al-hero__logo"
          src="/logo_1.png"
          alt="Astria"
          width={1254}
          height={1254}
          decoding="async"
        />
      </div>
    </div>
  );
};

/* Card headings stay in English in every language; only body text is translated. */

/* ---------- card grid ---------- */

type GridItem = { title: string; text?: string };

interface FeatureGridSectionProps {
  id: string;
  titleKey: HeadlineKey;
  content: {
    eyebrow?: string;
    body?: string;
    items: readonly GridItem[];
  };
  /** Shifts which planet glyph the first card gets. */
  glyphOffset?: number;
}

const columnModifier = (count: number): string => {
  if (count === 4) return "al-grid--4";
  if (count === 5) return "al-grid--5";
  return "";
};

export const FeatureGridSection: React.FC<FeatureGridSectionProps> = ({
  id,
  titleKey,
  content,
  glyphOffset = 0,
}) => {
  const { tr } = useLandingCopy();
  return (
    <SectionShell id={id}>
      <SectionHead eyebrow={content.eyebrow} titleKey={titleKey} body={content.body} />
      <div className={`al-grid ${columnModifier(content.items.length)}`}>
        {content.items.map(({ title, text }, index) => (
          <div key={title} className="al-card" data-reveal>
            <div className="al-card__glyph" aria-hidden="true">
              {PLANET_GLYPHS[(index + glyphOffset) % PLANET_GLYPHS.length]}
            </div>
            <span className="al-card__index">{pad2(index + 1)}</span>
            <h3 className="al-card__title">{tr(title)}</h3>
            {text && <p className="al-card__text">{tr(text)}</p>}
          </div>
        ))}
      </div>
    </SectionShell>
  );
};

/* ---------- calm guidance ---------- */

export const CalmGuidanceSection: React.FC = () => {
  const { tr, headline } = useLandingCopy();
  return (
    <SectionShell id="calm_guidance">
      <div className="al-statement" data-reveal>
        <span className="al-hairline" />
        <h2 className="al-h2">
          <Emphasis markup={headline("calmT")} />
        </h2>
        <span className="al-hairline" />
      </div>
      <div className="al-moments">
        {CALM_GUIDANCE.moments.map((moment) => (
          <div key={moment.label} className="al-moment" data-reveal>
            <b>{tr(moment.label)}</b>
            <p>{tr(moment.text)}</p>
          </div>
        ))}
      </div>
    </SectionShell>
  );
};

/* ---------- Life GPS ---------- */

const RING_CENTER = 120;
const RING_STROKE = 9;
const RING_GAP = 22;
const RING_OUTER_RADIUS = 104;

/** Decorative rings: each state's arc is a soft fraction of its track, never a score. */
const LifeGpsRings: React.FC = () => (
  <svg className="al-rings__svg" viewBox="0 0 240 240" aria-hidden="true" focusable="false">
    {LIFE_GPS.states.map((state, index) => {
      const radius = RING_OUTER_RADIUS - index * RING_GAP;
      const length = 2 * Math.PI * radius;
      return (
        <g key={state.label} className={`al-rings__ring al-rings__ring--${index + 1}`}>
          <circle
            className="al-rings__track"
            cx={RING_CENTER}
            cy={RING_CENTER}
            r={radius}
            strokeWidth={RING_STROKE}
          />
          <circle
            className="al-rings__arc"
            cx={RING_CENTER}
            cy={RING_CENTER}
            r={radius}
            strokeWidth={RING_STROKE}
            transform={`rotate(-90 ${RING_CENTER} ${RING_CENTER})`}
            style={
              {
                "--al-len": length.toFixed(2),
                "--al-off": (length * (1 - state.fill)).toFixed(2),
              } as React.CSSProperties
            }
          />
        </g>
      );
    })}
  </svg>
);

export const LifeGpsSection: React.FC = () => {
  const { tr } = useLandingCopy();
  return (
    <SectionShell id="life_gps">
      <div className="al-two">
        <div data-reveal>
          <div className="al-eyebrow">{tr(LIFE_GPS.eyebrow)}</div>
          <h2 className="al-h2">{tr(LIFE_GPS.headline)}</h2>
          <p className="al-lead">{tr(LIFE_GPS.sentence)}</p>
        </div>
        <div className="al-rings" data-reveal>
          <LifeGpsRings />
          <ul className="al-rings__legend">
            {LIFE_GPS.states.map((state, index) => (
              <li key={state.label}>
                <span className={`al-dot al-dot--${index + 1}`} aria-hidden="true" />
                <b>{tr(state.label)}</b>
                <em>{tr(state.sampleValue)}</em>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </SectionShell>
  );
};

/* ---------- Life Areas / steps ---------- */

export const LifeAreasSection: React.FC = () => {
  const { tr } = useLandingCopy();
  return (
    <SectionShell id="life_areas">
      <SectionHead eyebrow={LIFE_AREAS.eyebrow} titleKey="innerT" body={LIFE_AREAS.body} />
      <div className="al-areas">
        {LIFE_AREAS.groups.map((group, index) => (
          <div key={group.label} className="al-area" data-reveal>
            <b>{tr(group.label)}</b>
            <span className={`al-state al-state--${index + 1}`}>
              <i className="al-dot" aria-hidden="true" />
              {tr(group.sampleValue)}
            </span>
            <p>{tr(group.description)}</p>
          </div>
        ))}
      </div>
    </SectionShell>
  );
};

export const StepsSection: React.FC = () => {
  const { tr } = useLandingCopy();
  return (
    <SectionShell id="steps_to_clarity">
      <SectionHead eyebrow={STEPS_TO_CLARITY.eyebrow} titleKey="stepsT" />
      <div className="al-steps">
        {STEPS_TO_CLARITY.steps.map((step) => (
          <div key={step.number} className="al-step" data-reveal>
            <div className="al-step__number">{step.number}</div>
            <h3 className="al-step__title">{tr(step.title)}</h3>
            <p className="al-step__text">{tr(step.text)}</p>
          </div>
        ))}
      </div>
    </SectionShell>
  );
};

/* ---------- FAQ / closing banner ---------- */

export const FaqSection: React.FC = () => {
  const { tr } = useLandingCopy();
  return (
    <SectionShell id="faq">
      <SectionHead eyebrow={FAQ.eyebrow} titleKey="beforeT" />
      <div className="al-faq">
        {FAQ.items.map((item) => (
          <details key={item.q} className="al-faq__item" data-reveal>
            <summary>{tr(item.q)}</summary>
            <p>{tr(item.a)}</p>
          </details>
        ))}
      </div>
    </SectionShell>
  );
};

export const FinalCtaSection: React.FC<{ onAction: (action: string) => void }> = ({
  onAction,
}) => {
  const { tr, headline } = useLandingCopy();
  return (
    <SectionShell id="final_cta">
      <div className="al-final" data-reveal>
        <div className="al-eyebrow al-eyebrow--center">{tr(FINAL_CTA.eyebrow)}</div>
        <h2 className="al-h2">
          <Emphasis markup={headline("finalT")} />
        </h2>
        <button
          type="button"
          className="al-btn al-btn--primary"
          onClick={() => onAction(FINAL_CTA.cta.action)}
        >
          {headline("finalCta")} ✦
        </button>
      </div>
    </SectionShell>
  );
};
